import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ComponentCoach } from '@/components/nbct/ComponentCoach';
import { PropositionGuide } from '@/components/nbct/PropositionGuide';
import { 
  BookOpen, 
  Video, 
  Users, 
  CheckCircle, 
  Star, 
  FileText, 
  Monitor,
  PenTool,
  Trophy,
  Target,
  Clock,
  Award,
  Heart,
  User
} from 'lucide-react';

const NBCTCoaching = () => {
  const navigate = useNavigate();
  const [activeComponent, setActiveComponent] = useState<string | null>(null);
  const [activeProposition, setActiveProposition] = useState<string | null>(null);
  const [completedSections, setCompletedSections] = useState<string[]>([]);

  const corePropositions = [
    {
      id: '1',
      title: 'Committed to Students and Their Learning',
      description: 'Teachers are dedicated to making knowledge accessible to all students',
      icon: <Heart className="h-6 w-6" />,
      color: 'bg-blue-500'
    },
    {
      id: '2', 
      title: 'Content and Pedagogical Knowledge',
      description: 'Teachers know subjects and how to teach them effectively',
      icon: <BookOpen className="h-6 w-6" />,
      color: 'bg-green-500'
    },
    {
      id: '3',
      title: 'Managing and Monitoring Learning',
      description: 'Teachers orchestrate learning in group and individual settings',
      icon: <Target className="h-6 w-6" />,
      color: 'bg-purple-500'
    },
    {
      id: '4',
      title: 'Systematic Thinking and Learning',
      description: 'Teachers think systematically about practice and learn from experience',
      icon: <PenTool className="h-6 w-6" />,
      color: 'bg-orange-500'
    },
    {
      id: '5',
      title: 'Learning Communities',
      description: 'Teachers are members of learning communities',
      icon: <Users className="h-6 w-6" />,
      color: 'bg-teal-500'
    }
  ];

  const certificationComponents = [
    {
      id: 'component1',
      title: 'Component 1: Content Knowledge',
      description: 'Computer-based assessment of content knowledge and pedagogical practices',
      icon: <Monitor className="h-8 w-8" />,
      type: 'Computer-Based Assessment',
      duration: '3 hours',
      tasks: ['3 Constructed Response Exercises', '45 Selected Response Items'],
      status: 'not-started'
    },
    {
      id: 'component2', 
      title: 'Component 2: Differentiation in Instruction',
      description: 'Portfolio entry analyzing student needs and instructional differentiation',
      icon: <Users className="h-8 w-8" />,
      type: 'Portfolio Entry',
      duration: '2-3 months',
      tasks: ['Student Analysis', 'Instructional Planning', 'Work Samples', 'Written Commentary'],
      status: 'not-started'
    },
    {
      id: 'component3',
      title: 'Component 3: Teaching Practice and Learning Environment', 
      description: 'Video-based portfolio showing classroom interactions and teaching',
      icon: <Video className="h-8 w-8" />,
      type: 'Video Portfolio',
      duration: '2-3 months',
      tasks: ['Video Recording', 'Student Interaction Analysis', 'Written Commentary'],
      status: 'not-started'
    },
    {
      id: 'component4',
      title: 'Component 4: Effective and Reflective Practitioner',
      description: 'Portfolio demonstrating professional growth and collaboration',
      icon: <Star className="h-8 w-8" />,
      type: 'Portfolio Entry', 
      duration: '2-3 months',
      tasks: ['Student Knowledge Application', 'Assessment Planning', 'Professional Collaboration'],
      status: 'not-started'
    }
  ];

  const progressData = {
    overall: 25,
    component1: 0,
    component2: 15,
    component3: 0,
    component4: 45
  };

  // Show active component coach
  if (activeComponent) {
    return (
      <div className="container mx-auto p-6">
        <ComponentCoach 
          componentId={activeComponent} 
          onClose={() => setActiveComponent(null)} 
        />
      </div>
    );
  }

  // Show active proposition guide
  if (activeProposition) {
    return (
      <div className="container mx-auto p-6">
        <PropositionGuide 
          propositionId={activeProposition} 
          onClose={() => setActiveProposition(null)} 
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-3">
          <Trophy className="h-12 w-12 text-primary" />
          <h1 className="text-4xl font-bold">National Board Certified Teacher Coach</h1>
        </div>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Your comprehensive guide to achieving National Board Certification based on the Five Core Propositions
        </p>
        <div className="flex items-center justify-center space-x-4">
          <Badge variant="secondary" className="text-sm">
            <Award className="h-4 w-4 mr-1" />
            Professional Certification
          </Badge>
          <Badge variant="outline" className="text-sm">
            <Clock className="h-4 w-4 mr-1" />
            12-18 Month Journey
          </Badge>
        </div>
      </div>

      {/* Overall Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Your Certification Progress</CardTitle>
          <CardDescription>Track your journey toward National Board Certification</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-muted-foreground">{progressData.overall}%</span>
            </div>
            <Progress value={progressData.overall} className="h-3" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              {certificationComponents.map((component) => (
                <div key={component.id} className="text-center">
                  <div className="text-sm font-medium">{component.title.split(':')[0]}</div>
                  <Progress 
                    value={progressData[component.id as keyof typeof progressData]} 
                    className="h-2 mt-1" 
                  />
                  <div className="text-xs text-muted-foreground mt-1">
                    {progressData[component.id as keyof typeof progressData]}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="propositions">Core Propositions</TabsTrigger>
          <TabsTrigger value="components">Components</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Certification Overview</CardTitle>
                <CardDescription>Understanding the National Board process</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Standards-Based Evidence</p>
                    <p className="text-sm text-muted-foreground">
                      Demonstrate accomplished teaching through four comprehensive components
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Portfolio Assessment</p>
                    <p className="text-sm text-muted-foreground">
                      Submit evidence of your teaching practice and student impact
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Computer-Based Assessment</p>
                    <p className="text-sm text-muted-foreground">
                      Demonstrate content knowledge and pedagogical understanding
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Portfolio Integration</p>
                    <p className="text-sm text-muted-foreground">
                      Store and organize your NBCT evidence examples in your <strong>My Portfolio</strong> space for easy access and sharing
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Getting Started</CardTitle>
                <CardDescription>Your first steps toward certification</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  className="w-full" 
                  variant="default"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = '/resources/NBPTS-Core-Propositions.pdf';
                    link.download = 'NBPTS-Core-Propositions.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Download Core Propositions Guide
                </Button>
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => window.open('https://www.nbpts.org/wp-content/uploads/2017/07/NBCT_candidate_center_preparing_for_certification_components.pdf', '_blank')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Component Preparation Guide
                </Button>
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => window.open('https://www.nbpts.org/certification/certificate-areas/', '_blank')}
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Explore Certificate Areas
                </Button>
                <Button 
                  className="w-full" 
                  variant="secondary"
                  onClick={() => navigate('/my-portfolio')}
                >
                  <User className="h-4 w-4 mr-2" />
                  Go to My Portfolio
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="propositions" className="space-y-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">The Five Core Propositions</h2>
            <p className="text-muted-foreground">
              The foundation of accomplished teaching and National Board Standards
            </p>
          </div>
          
          <div className="grid gap-6">
            {corePropositions.map((proposition) => (
              <Card key={proposition.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${proposition.color} text-white`}>
                      {proposition.icon}
                    </div>
                    <div>
                      <CardTitle className="text-lg">
                        Proposition {proposition.id}: {proposition.title}
                      </CardTitle>
                      <CardDescription>{proposition.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setActiveProposition(proposition.id)}
                  >
                    Explore Proposition {proposition.id}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="components" className="space-y-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Certification Components</h2>
            <p className="text-muted-foreground">
              Four comprehensive assessments that demonstrate accomplished teaching
            </p>
          </div>

          <div className="grid gap-6">
            {certificationComponents.map((component) => (
              <Card key={component.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-lg bg-primary text-primary-foreground">
                        {component.icon}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{component.title}</CardTitle>
                        <CardDescription>{component.description}</CardDescription>
                        <div className="flex items-center space-x-4 mt-2">
                          <Badge variant="secondary">{component.type}</Badge>
                          <Badge variant="outline">
                            <Clock className="h-3 w-3 mr-1" />
                            {component.duration}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Progress 
                      value={progressData[component.id as keyof typeof progressData]} 
                      className="w-24 h-2" 
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Key Tasks:</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {component.tasks.map((task, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <div className="h-1.5 w-1.5 bg-muted-foreground rounded-full" />
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className="w-full mt-4"
                      onClick={() => setActiveComponent(component.id)}
                    >
                      Start {component.title.split(':')[0]}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="resources" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-5 w-5" />
                  <span>Official Documents</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => window.open('/resources/NBPTS-Core-Propositions.pdf', '_blank')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Core Propositions PDF
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Certificate Standards
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Portfolio Instructions
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Video className="h-5 w-5" />
                  <span>Video Resources</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <Video className="h-4 w-4 mr-2" />
                  Component Overviews
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Video className="h-4 w-4 mr-2" />
                  Sample Submissions
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Video className="h-4 w-4 mr-2" />
                  Scoring Rubrics
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5" />
                  <span>Portfolio & Support</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/my-portfolio')}
                >
                  <User className="h-4 w-4 mr-2" />
                  My Portfolio - Store NBCT Evidence
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Users className="h-4 w-4 mr-2" />
                  Find a Mentor
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Users className="h-4 w-4 mr-2" />
                  Join Study Groups
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Users className="h-4 w-4 mr-2" />
                  Professional Learning
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NBCTCoaching;